import {getParameter, SOURCE_PARAM_KEY} from '../lib/params.ts'
import {readStorage, writeStorage} from '../lib/storage.ts'

/** Local storage key of the time (ms since epoch) the visitor dismissed the CTA */
const DISMISSED_AT_KEY = 'download-cta-dismissed-at'
/** How long the CTA stays away once dismissed */
const DISMISS_DURATION = 7 * 24 * 60 * 60 * 1000
/** When the CTA bounces to draw attention, counted from being shown, unless it was opened by then */
const BOUNCE_DELAYS = [10_000, 20_000, 40_000]
/** Hover intent: keeps the CTA from opening or closing when the cursor merely passes by */
const HOVER_OPEN_DELAY = 200
const HOVER_CLOSE_DELAY = 400

/**
 * The download CTA at the bottom of posts and pages (`partials/download-cta.hbs`). Shown once scrolled past 300px while the cookie banner isn't, and
 * removed for visitors who came from the app or dismissed it recently.
 */
export function initDownloadCta() {
    const cta = document.getElementById('downloadCta')
    const wrapper = cta?.parentElement
    if (!cta || !wrapper) return
    if (getParameter(SOURCE_PARAM_KEY) === 'photone' || dismissedRecently()) {
        wrapper.remove()
        return
    }

    let wasOpened = false
    const isOpen = () => !cta.classList.contains('closed')
    const setOpen = (open: boolean) => {
        wasOpened ||= open
        cta.classList.toggle('closed', !open)
    }

    cta.querySelectorAll('.downloadCtaToggle').forEach(button => button.addEventListener('click', () => setOpen(!isOpen())))
    cta.querySelector('.downloadCtaRemove')?.addEventListener('click', () => {
        writeStorage(DISMISSED_AT_KEY, String(Date.now()))
        wrapper.remove()
    })
    if (matchMedia('(hover: hover)').matches) openOnHover(cta, setOpen)

    const showOnceScrolled = () => {
        if (scrollY <= 300 || document.getElementById('cookieBanner')?.classList.contains('active')) return
        cta.classList.add('visible')
        removeEventListener('scroll', showOnceScrolled)
        for (const delay of BOUNCE_DELAYS) setTimeout(() => {
            if (!wasOpened) bounce(cta)
        }, delay)
    }
    addEventListener('scroll', showOnceScrolled, {passive: true})
}

/** Whether the visitor dismissed the CTA less than `DISMISS_DURATION` ago */
function dismissedRecently() {
    return Date.now() - Number(readStorage(DISMISSED_AT_KEY)) < DISMISS_DURATION // false if never dismissed: Number(null) is 0
}

/** Opens the CTA while the cursor rests on its "Explore Photone" area (not the close button) and closes it once the cursor left the CTA */
function openOnHover(cta: HTMLElement, setOpen: (open: boolean) => void) {
    const area = cta.querySelector('[data-download-cta-hover]')
    let timer: ReturnType<typeof setTimeout> | undefined
    const setOpenDelayed = (open: boolean, delay: number) => {
        clearTimeout(timer)
        timer = setTimeout(() => setOpen(open), delay)
    }

    area?.addEventListener('mouseenter', () => setOpenDelayed(true, HOVER_OPEN_DELAY))
    area?.addEventListener('mouseleave', () => clearTimeout(timer))
    cta.addEventListener('mouseenter', () => clearTimeout(timer))
    cta.addEventListener('mouseleave', () => setOpenDelayed(false, HOVER_CLOSE_DELAY))
}

/** Plays the bounce animation of `download-cta.scss` */
function bounce(cta: HTMLElement) {
    cta.classList.add('bouncing')
    cta.addEventListener('animationend', () => cta.classList.remove('bouncing'), {once: true})
}
